import {
  takeLeading, put, select, call,
  takeLatest,
  takeEvery,
  race,
  take,
} from 'redux-saga/effects';
import { api } from '@/utils/api';
import studioActions from './studio.actions';
import { StudioActionTypes } from './studio.types';
import { config, STUDIO_DOMAIN } from '@/config';
import { isBRServer } from '@/utils/Validators/validation';
import logger from '@/utils/logger';
import studioAssembly from './studio.assembly';
import { HgProducts } from '@/components/SupportForms/SupportForms.types';
import { preChatActions } from '../preChat';

const { WHMCS_URL } = config;

function* getStudioAccount() {
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: `${WHMCS_URL}/api/v3/studio/account`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-service-token': jwtToken,
      brand,
    },
  };

  yield put(studioActions.loading(true));

  try {
    const response = yield call(api.whmcs.get, request);

    yield put(studioActions.account.success());

    yield put(studioActions.account.set(response.data));
  } catch (requestError) {
    const error = { ...requestError, userRequestData: request };
    logger.error('Error sagas studio - method getStudioAccount', error);
    yield put(studioActions.account.failure());
  }

  yield put(studioActions.loading(false));
}

function* getStudioAccessTracking() {
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: `${WHMCS_URL}/api/v3/studio/access`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-service-token': jwtToken,
      brand,
    },
  };

  try {
    yield call(api.whmcs.patch, request);

    yield put(studioActions.accessTracking.success());
  } catch (requestError) {
    const error = { ...requestError, userRequestData: request };
    logger.error('Error sagas studio - method getStudioAccessTracking', error);

    yield put(studioActions.accessTracking.failure());
  }
}

function* getFormCheck() {
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: `${WHMCS_URL}/api/v3/questionnaire/hg-studio/answers/check/HgStudioInitialForm`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-service-token': jwtToken,
      brand,
    },
  };

  yield put(studioActions.loading(true));

  try {
    const response = yield call(api.whmcs.get, request);
    yield put(studioActions.formCheck.success());

    yield put(studioActions.formCheck.set(response.data));
  } catch (requestError) {
    const error = { ...requestError, userRequestData: request };
    logger.error('Error sagas studio - method getFormCheck', error);
    yield put(studioActions.formCheck.failure());
  }

  yield put(studioActions.loading(false));
}

function* sendQuestions(action) {
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const brand = isBRServer ? 'br' : 'es';
  const { answer1, answer2, answer3 } = action.payload;

  const request = {
    url: `${WHMCS_URL}/api/v3/questionnaire/answers`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-service-token': jwtToken,
      brand,
    },
    data: {
      project: 'hg-studio',
      source: 'HgStudioInitialForm',
      answers: [
        {
          question: 'studio-creation-goals',
          answer_text: answer1,
        },
        {
          question: 'business-project-area',
          answer_text: answer2,
        },
        {
          question: 'used-design-platforms',
          answer_text: answer3,
        },
      ],
    },
  };

  yield put(studioActions.loading(true));

  try {
    const response = yield call(api.whmcs.post, request);
    yield put(studioActions.formSend.success());

    yield put(studioActions.formSend.set(response.data));
  } catch (requestError) {
    const error = { ...requestError, userRequestData: request };
    logger.error('Error sagas studio - method sendQuestions', error);
    yield put(studioActions.formSend.failure());
  }

  yield put(studioActions.loading(false));
}

function* requestForMe() {
  const { token, jwt: jwtToken } = yield select(state => state.auth);

  const brand = isBRServer ? 'br' : 'es';
  const request = {
    url: `${WHMCS_URL}/api/v3/studio/formatsForMe`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-service-token': jwtToken,
      brand,
    },
  };

  yield put(studioActions.forMe.loading(true));

  try {
    const response = yield call(api.whmcs.get, request);
    yield put(studioActions.forMe.set(response.data));

    yield put(studioActions.forMe.success());
    yield put(studioActions.forMe.loading(false));
  } catch (requestError) {
    yield put(studioActions.forMe.loading(false));

    const error = { ...requestError, userRequestData: request };
    logger.error('Error sagas studio - method requestForMe', error);
    yield put(studioActions.forMe.failure());
  }
}

function* requestTemplates(action) {
  const { payload } = action;
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const { templates } = yield select(state => state.studio);
  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: `${WHMCS_URL}/api/v3/studio/templates?formats=${payload}`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-service-token': jwtToken,
      brand,
    },
  };

  yield put(studioActions.templates.loading(true));

  try {
    const response = yield call(api.whmcs.get, request);
    yield put(studioActions.templates.success());

    const newTemplates = {
      ...templates,
      ...(typeof response.data === 'object' && response.data !== null ? response.data : {}),
    };

    yield put(studioActions.templates.set(newTemplates));
  } catch (requestError) {
    const error = { ...requestError, userRequestData: request };
    logger.error('Error sagas studio - method requestTemplates', error);
    yield put(studioActions.templates.failure());
  }

  yield put(studioActions.templates.loading(false));
}

function* requestLogo(action) {
  const { payload } = action;
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const { listLogos } = yield select(state => state.studio);
  const brand = isBRServer ? 'br' : 'es';

  const controller = new AbortController();

  const request = {
    url: `${WHMCS_URL}/api/v3/studio/logo`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-service-token': jwtToken,
      brand,
    },
    data: payload,
    signal: controller.signal,
  };

  yield put(studioActions.logoGeneration.loading(true));

  try {
    const { response, cancel } = yield race({
      response: call(api.whmcs.post, request),
      cancel: take(StudioActionTypes.STUDIO_LOGO_GENERATION_CANCEL),
    });

    if (cancel) {
      controller.abort();
      yield put(studioActions.logoGeneration.loading(false));
      return;
    }

    yield put(studioActions.logoGeneration.success());

    const newListLogos = {
      ...listLogos,
      ...(typeof response.data === 'object' && response.data !== null ? response.data : {}),
    };

    yield put(studioActions.logoGeneration.set(newListLogos));
  } catch (requestError) {
    const error = { ...requestError, userRequestData: request };
    logger.error('Error sagas logoBuilder - method requestLogo', error);
    yield put(studioActions.logoGeneration.failure());
  }

  yield put(studioActions.logoGeneration.loading(false));
}

function* getLogo(action) {
  const { payload } = action;
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const brand = isBRServer ? 'br' : 'es';
  const filename = `logo-${payload}.png`;

  const request = {
    url: `${WHMCS_URL}/api/v3/studio/downloadLogo/${payload}`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-service-token': jwtToken,
      brand,
    },
  };

  try {
    const response = yield call(fetch, request.url, {
      method: 'GET',
      headers: request.headers,
    });

    studioAssembly.downloadFileResponse(yield response.blob(), filename);

    yield put(studioActions.logoDownload.success(payload));
  } catch (requestError) {
    const error = { ...requestError, userRequestData: request };
    logger.error('Error sagas studio - method getLogo', error);
    yield put(studioActions.logoDownload.failure(payload));
  }
}

function* requestProducts() {
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: `${WHMCS_URL}/api/v3/studio/products`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-service-token': jwtToken,
      brand,
    },
  };

  yield put(studioActions.products.loading(true));

  try {
    const response = yield call(api.whmcs.get, request);
    yield put(studioActions.products.success());

    yield put(studioActions.products.set(response.data));
    yield put(studioActions.products.loading(false));
  } catch (requestError) {
    yield put(studioActions.products.loading(false));
    const error = { ...requestError, userRequestData: request };
    logger.error('Error sagas studio - method requestProducts', error);
    yield put(studioActions.products.failure());
  }
}

function* requestProvisionFreePlan(action) {
  const { payload } = action;
  const { token, jwt: jwtToken } = yield select(state => state.auth);

  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: `${WHMCS_URL}/api/v3/fast-provisioning`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-service-token': jwtToken,
      brand,
    },
    data: {
      product_id: HgProducts.STUDIO_FREE,
    },
  };

  yield put(studioActions.provisionFree.loading(true));

  try {
    yield call(api.whmcs.post, request);
    yield put(studioActions.provisionFree.success());
    yield put(studioActions.account.request());
    yield put(preChatActions.productByStatus.request({ withCancelled: true, withPending: true }));

    payload && payload.onSuccessCallback && payload.onSuccessCallback();
  } catch (requestError) {
    const error = { ...requestError, userRequestData: request };
    logger.error('Error sagas studio - method requestProvisionFreePlan', error);
    yield put(studioActions.provisionFree.failure());
  }

  yield put(studioActions.provisionFree.loading(false));
}

function* requestCreateDocument(action) {
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const brand = isBRServer ? 'br' : 'es';
  const {
    docType, id, accessToken, popupBlockedAction,
  } = action.payload;

  const data = docType === 'template' ? { template_id: id } : { format_id: id };

  const request = {
    url: `${WHMCS_URL}/api/v3/studio/documents`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-service-token': jwtToken,
      brand,
    },
    data,
  };

  yield put(studioActions.documentCreation.loading(true));

  try {
    const response = yield call(api.whmcs.post, request);
    yield put(studioActions.documentCreation.success());

    if (response.data && response.data.document_id) {
      const urlToOpen = `https://editor.${STUDIO_DOMAIN}/editor/${response.data.document_id}/${accessToken}`;
      const newWindow = window.open(urlToOpen, '_blank');

      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        popupBlockedAction && popupBlockedAction();
      }
    } else {
      logger.error('Error sagas studio - method requestCreateDocument - Invalid response', {
        response,
        userRequestData: request,
      });
    }
  } catch (requestError) {
    const error = { ...requestError, userRequestData: request };
    logger.error('Error sagas studio - method requestCreateDocument', error);
    yield put(studioActions.documentCreation.failure());
  }

  yield put(studioActions.documentCreation.loading(false));
}

function* sagas() {
  yield takeLeading(StudioActionTypes.STUDIO_ACCOUNT_REQUEST, getStudioAccount);
  yield takeLatest(StudioActionTypes.STUDIO_ACCESS_TRACKING_REQUEST, getStudioAccessTracking);
  yield takeLatest(StudioActionTypes.STUDIO_FORM_CHECK_REQUEST, getFormCheck);
  yield takeLatest(StudioActionTypes.STUDIO_FORM_SEND_REQUEST, sendQuestions);
  yield takeLatest(StudioActionTypes.STUDIO_GET_TEMPLATES_FOR_ME, requestForMe);
  yield takeEvery(StudioActionTypes.STUDIO_GET_TEMPLATES_REQUEST, requestTemplates);
  yield takeLatest(StudioActionTypes.STUDIO_LOGO_GENERATION_REQUEST, requestLogo);
  yield takeLatest(StudioActionTypes.STUDIO_PRODUCTS_REQUEST, requestProducts);
  yield takeEvery(StudioActionTypes.STUDIO_LOGO_DOWNLOAD_REQUEST, getLogo);
  yield takeLatest(StudioActionTypes.STUDIO_REQUEST_PROVISION_FREE_PLAN, requestProvisionFreePlan);
  yield takeLatest(StudioActionTypes.STUDIO_DOCUMENT_CREATION_REQUEST, requestCreateDocument);
}

export default sagas;
